import {
  QueryFunction,
  useMutation,
  UseMutationResult,
  useQuery,
  UseQueryResult,
} from 'react-query'
import { AxiosError } from 'axios'

import {
  FlowSessionState,
  FlowSessionStateRequest,
  SearchSessionsParams,
  SearchSessionsResponse,
} from 'common/interfaces'
import api from 'utils/api'
import { useFlow, useFlowSlug } from 'utils/flow'
import { useLocalStorage } from 'utils/hook'

const createFlowSession: QueryFunction<FlowSessionState> = async ({ queryKey }) => {
  const [currentFlowId, startPageId] = queryKey
  const response = await api.post('flow_sessions/', {
    flow: currentFlowId,
    current_page: startPageId,
  })

  return response.data
}

const fetchSearchSessions = async (
  params: SearchSessionsParams,
  cursor: string | null
): Promise<SearchSessionsResponse> => {
  const response = await api.get('search_sessions/', {
    params: { ...params, cursor },
  })
  return response.data
}

export const useSearchSessions = (
  params: SearchSessionsParams,
  cursor: string | null
): UseQueryResult<SearchSessionsResponse, AxiosError> => {
  return useQuery(
    ['search_sessions', { ...params, cursor }],
    // eslint-disable-next-line @typescript-eslint/return-await
    async () => fetchSearchSessions({ ...params }, cursor),
    {
      enabled: !!params.flow_slug && !!params.company_slug,
      keepPreviousData: true,
      onError: (error) => console.error(error),
    }
  )
}

export const useCreateFlowSession = (): UseQueryResult<FlowSessionState, AxiosError> => {
  const { data } = useFlow()
  const flowSlug = useFlowSlug()

  const queryParams = new URLSearchParams(location.search)
  const SIDParam = queryParams.get('s_id')
  const previewID = queryParams.get('preview_id')
  const [sessionFlowId, setSessionFlowIdLS] = useLocalStorage<FlowSessionState['id'] | null>(
    `${flowSlug}/sessionFlowId`,
    null
  )

  const queryResult = useQuery<FlowSessionState, AxiosError>(
    [data!.id, data!.start_page.id],
    createFlowSession,
    {
      enabled: (SIDParam === null && sessionFlowId === null && previewID === null) ?? false,
      onSuccess: (data) => {
        setSessionFlowIdLS(data.id)
      },
      onError: (error) => console.error(error),
    }
  )

  return queryResult
}

const updateFlowSession = async (
  requestVars: FlowSessionStateRequest
): Promise<FlowSessionState> => {
  const { id: sID, ...body } = requestVars
  const response = await api.patch(`flow_sessions/${sID}/`, body)
  return response.data
}

export const useUpdateFlowSession = (): UseMutationResult<
  FlowSessionState,
  unknown,
  FlowSessionStateRequest
> => {
  return useMutation<FlowSessionState, unknown, FlowSessionStateRequest>(updateFlowSession)
}
